/**
 * Custom LineChartComponent - Override Component
 * Auto generated overrides from shared library
 */
import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as echarts from 'echarts';
import { LineChartComponent as ParentLineChartComponent } from '@shared-lib/chart-components/line-chart/line-chart.component';
import { CapitalCasePipe } from '@shared-lib/pipes/capital-case.pipe';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { ThemeService } from '@shared-lib/shared-services/theme.service';
import { buildConfigurableAxisLabel, buildConfigurableTooltipFormatter } from '@shared-lib/utils/chart.utils';

@Component({
  selector: 'app-line-chart',
  standalone: true,
  imports: [CommonModule, FormsModule, CapitalCasePipe],
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.scss']
})
export class LineChartComponent extends ParentLineChartComponent {
  // Constructor is inherited from parent to avoid dependency injection issues
  private readonly customConfigService = inject(ConfigService);
  private readonly customThemeService = inject(ThemeService);
  availablePriceDynamicsYears: string[] = [];
  selectedPriceDynamicsFromYear = '';
  selectedPriceDynamicsToYear = '';
  priceDynamicsSubtitle = '';
  priceDynamicsFooterMetrics: Array<{ label: string; value: string; highlight?: string }> = [];
  private latestMultiSeriesFilters: any;
  private lastPriceDynamicsDataRef: any = null;

  protected override loadAndRenderChart(filters?: any): any {
    if (this.data?.chartConfig?.multiSeries === true) {
      const chartDom = document.getElementById(this.data?.widgetId);
      if (!chartDom) return;

      if (!this.myChart) {
        this.myChart = echarts.init(chartDom, 'dvm');
      }

      if (this.lastPriceDynamicsDataRef !== this.data) {
        this.resetPriceDynamicsYearState();
        this.lastPriceDynamicsDataRef = this.data;
      }

      this.latestMultiSeriesFilters = filters;
      this.initializePriceDynamicsYearState();
      const requestConfig = this.buildMultiSeriesRequestConfig();
      this.myChart.showLoading();
      this.customConfigService.getConfig(requestConfig.apiUrl, requestConfig, filters)?.subscribe({
        next: (res: any) => {
          const raw = this.resolveMultiSeriesPayload(res);
          this.applyPriceDynamicsPresentation(raw);
          this.renderChart(raw);
          this.addResizeListener();
        },
        error: () => this.myChart?.showLoading(),
      });
      return;
    }

    return super.loadAndRenderChart(filters);
  }

  protected override renderChart(chartData?: any): any {
    if (this.data?.chartConfig?.multiSeries === true) {
      const payload = this.resolveMultiSeriesPayload(chartData);
      this.applyPriceDynamicsPresentation(payload);
      const categories = payload?.categories || payload?.category || [];
      const seriesConfig = Array.isArray(payload?.series) ? payload.series : [];
      const theme = this.customThemeService.resolve(this.data?.themeOverrides);
      const tooltipCfg = this.data?.chartConfig?.tooltip;
      const formatter = buildConfigurableTooltipFormatter(tooltipCfg);
      const yAxisLabel = buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis);
      const showDataLabels = this.data?.chartConfig?.showDataLabels === true;

      this.allCategories = categories;

      const option = {
        color: seriesConfig.map((series: any) => series.color).filter(Boolean),
        tooltip: {
          show: tooltipCfg?.show !== false,
          trigger: tooltipCfg?.trigger || 'axis',
          backgroundColor: theme.tooltip?.bg || '#fff',
          borderColor: theme.tooltip?.borderColor || '#ccc',
          textStyle: {
            color: theme.tooltip?.textColor || '#333',
            fontSize: theme.tooltip?.fontSize || 13,
          },
          ...(formatter ? { formatter } : {}),
        },
        legend: {
          show: this.data?.chartConfig?.showLegend !== false,
          bottom: 0,
          left: 0,
          icon: 'circle',
          itemWidth: this.data?.chartConfig?.legendItemWidth || 8,
          itemHeight: this.data?.chartConfig?.legendItemHeight || 8,
          itemGap: this.data?.chartConfig?.legendItemGap || 12,
          textStyle: {
            color: '#60708a',
            fontSize: this.data?.chartConfig?.legendFontSize || 11,
            fontWeight: 700,
          },
        },
        grid: {
          left: this.data?.chartConfig?.gridLeft || 42,
          right: this.data?.chartConfig?.gridRight || 22,
          top: this.data?.chartConfig?.gridTop || 18,
          bottom: this.data?.chartConfig?.gridBottom || 54,
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: categories,
          name: this.data?.chartConfig?.xAxisName || '',
          nameLocation: 'middle',
          nameGap: 28,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#d9e2ef' } },
          axisLabel: {
            color: '#8a9bb3',
            fontSize: this.data?.chartConfig?.axisFontSize || 11,
            fontWeight: 700,
          },
        },
        yAxis: {
          type: 'value',
          min: this.data?.chartConfig?.yAxis?.min,
          max: this.data?.chartConfig?.yAxis?.max,
          interval: this.data?.chartConfig?.yAxis?.interval,
          axisLabel: {
            ...(yAxisLabel || {}),
            color: '#8a9bb3',
            fontSize: this.data?.chartConfig?.axisFontSize || 11,
            fontWeight: 700,
          },
          splitLine: { lineStyle: { color: '#dbe4ef' } },
        },
        series: seriesConfig.map((series: any) => ({
          name: series.name,
          type: 'line',
          data: series.data || [],
          smooth: series.smooth === true,
          connectNulls: series.connectNulls ?? this.data?.chartConfig?.connectNulls ?? true,
          symbol: series.symbol || 'circle',
          symbolSize: series.symbolSize || 7,
          lineStyle: {
            width: series.lineWidth || 2,
            color: series.color,
          },
          itemStyle: {
            color: series.color,
            borderColor: series.borderColor || series.color,
            borderWidth: series.borderWidth ?? 1,
          },
          label: {
            show: showDataLabels && Array.isArray(series.labels),
            formatter: (params: any) => series.labels?.[params.dataIndex] || '',
            position: 'top',
            color: series.labelColor || series.color,
            fontSize: this.data?.chartConfig?.dataLabelFontSize || 11,
            fontWeight: 800,
          },
        })),
      };

      this.myChart.setOption(option, true);
      setTimeout(() => this.myChart?.hideLoading(), 150);
      return;
    }

    return super.renderChart(chartData);
  }

  onPriceDynamicsFromYearChange(year: string): void {
    year = String(year || '').trim();
    if (!year) {
      return;
    }

    this.selectedPriceDynamicsFromYear = year;
    if (this.selectedPriceDynamicsToYear && Number(this.selectedPriceDynamicsFromYear) > Number(this.selectedPriceDynamicsToYear)) {
      this.selectedPriceDynamicsToYear = year;
    }
    this.loadAndRenderChart(this.latestMultiSeriesFilters);
  }

  onPriceDynamicsToYearChange(year: string): void {
    year = String(year || '').trim();
    if (!year) {
      return;
    }

    this.selectedPriceDynamicsToYear = year;
    if (this.selectedPriceDynamicsFromYear && Number(this.selectedPriceDynamicsToYear) < Number(this.selectedPriceDynamicsFromYear)) {
      this.selectedPriceDynamicsFromYear = year;
    }
    this.loadAndRenderChart(this.latestMultiSeriesFilters);
  }

  getAvailableFromYears(): string[] {
    if (!this.availablePriceDynamicsYears.length) {
      return [];
    }

    if (!this.selectedPriceDynamicsToYear) {
      return this.availablePriceDynamicsYears;
    }

    const maxYear = Number(this.selectedPriceDynamicsToYear);
    return this.availablePriceDynamicsYears.filter((year) => Number(year) <= maxYear);
  }

  getAvailableToYears(): string[] {
    if (!this.availablePriceDynamicsYears.length) {
      return [];
    }

    if (!this.selectedPriceDynamicsFromYear) {
      return this.availablePriceDynamicsYears;
    }

    const minYear = Number(this.selectedPriceDynamicsFromYear);
    return this.availablePriceDynamicsYears.filter((year) => Number(year) >= minYear);
  }

  private resolveMultiSeriesPayload(source: any): any {
    if (!source) return {};

    const mapped = this.mapPriceDynamicsResponse(source);
    if (mapped) {
      return mapped;
    }

    if (Array.isArray(source?.responseList) && source.responseList.length > 0) {
      const first = source.responseList[0];
      if (Array.isArray(first?.data) && first.data.length > 0) {
        return this.resolveMultiSeriesPayload(first.data[0]);
      }
    }

    if (Array.isArray(source?.data) && source.data.length > 0) {
      return this.resolveMultiSeriesPayload(source.data[0]);
    }

    if (source?.priceDynamics) {
      return source.priceDynamics;
    }

    if (this.data?.dataKey && source?.[this.data.dataKey]) {
      return source[this.data.dataKey];
    }

    return source;
  }

  private initializePriceDynamicsYearState(): void {
    if (this.availablePriceDynamicsYears.length > 0) {
      return;
    }

    const range = this.data?.chartConfig?.yearRange || {};
    const startYear = Number(range?.start || 2021);
    const endYear = Number(range?.end || 2025);
    if (!Number.isFinite(startYear) || !Number.isFinite(endYear) || endYear < startYear) {
      this.availablePriceDynamicsYears = [];
      this.selectedPriceDynamicsFromYear = '';
      this.selectedPriceDynamicsToYear = '';
      return;
    }

    this.availablePriceDynamicsYears = Array.from(
      { length: endYear - startYear + 1 },
      (_, index) => String(startYear + index),
    );
    if (!this.selectedPriceDynamicsFromYear) {
      this.selectedPriceDynamicsFromYear = String(startYear);
    }
    if (!this.selectedPriceDynamicsToYear) {
      this.selectedPriceDynamicsToYear = String(endYear);
    }
  }

  private resetPriceDynamicsYearState(): void {
    this.availablePriceDynamicsYears = [];
    this.selectedPriceDynamicsFromYear = '';
    this.selectedPriceDynamicsToYear = '';
  }

  private buildMultiSeriesRequestConfig(): any {
    const cloned = this.cloneValue(this.data || {});
    const filter = cloned?.customPayload?.requestData?.filter;
    if (!filter || typeof filter !== 'object') {
      return cloned;
    }

    const startYear = this.selectedPriceDynamicsFromYear || this.availablePriceDynamicsYears[0] || '2021';
    const endYear = this.selectedPriceDynamicsToYear || this.availablePriceDynamicsYears[this.availablePriceDynamicsYears.length - 1] || startYear;
    filter.startDate = this.replaceDateYear(String(filter.startDate || '01-01-2021'), startYear, '01-01');
    filter.endDate = this.replaceDateYear(String(filter.endDate || '12-31-2025'), endYear, '12-31');
    return cloned;
  }

  private replaceDateYear(value: string, year: string, fallbackMonthDay: string): string {
    const safeYear = String(year || '').trim();
    if (!safeYear) {
      return value;
    }

    const parts = String(value || '').split('-');
    if (parts.length === 3) {
      return `${parts[0]}-${parts[1]}-${safeYear}`;
    }

    return `${fallbackMonthDay}-${safeYear}`;
  }

  private mapPriceDynamicsResponse(source: any): any | null {
    const payload =
      source?.responseList?.[0]?.responseData?.[0] ||
      source?.responseData?.[0] ||
      null;

    if (!payload || typeof payload !== 'object' || !Array.isArray(payload?.data)) {
      return null;
    }

    const rows = payload.data;
    const categories = rows.map((item: any) => `${item?.year ?? ''} ${item?.period ?? ''}`.trim());
    const cmiSeriesName = this.buildPriceDynamicsSeriesName();
    const cmiSeriesData = rows.map((item: any) => {
      const ownValue = item?.priceDynamicsData?.rebuildPctChange;
      return ownValue ?? item?.priceDynamicsData?.cphPctChange ?? null;
    });
    const catSeriesData = rows.map((item: any) => item?.competitorData?.CAT ?? null);
    const mtuSeriesData = rows.map((item: any) => item?.competitorData?.MTU ?? null);
    const latestCmi = this.findLatestNumericValue(cmiSeriesData);
    const latestCompetitor = this.findLatestBestCompetitor(rows);
    const competitorGap = Number.isFinite(latestCmi) && Number.isFinite(latestCompetitor)
      ? latestCmi - latestCompetitor
      : NaN;

    return {
      categories,
      series: [
        {
          name: 'Construction PPI %',
          color: '#cbd5e1',
          symbol: 'circle',
          symbolSize: 6,
          borderColor: '#cbd5e1',
          borderWidth: 2,
          data: rows.map((item: any) => item?.ppiData?.constructionMachineryPct ?? null),
        },
        {
          name: 'Mining PPI %',
          color: '#4f7df3',
          symbol: 'diamond',
          symbolSize: 8,
          data: rows.map((item: any) => item?.ppiData?.miningMachineryPct ?? null),
        },
        {
          name: cmiSeriesName,
          color: '#dc0000',
          symbol: 'circle',
          symbolSize: 8,
          data: cmiSeriesData,
        },
        {
          name: 'CAT % Change',
          color: '#f59e0b',
          symbol: 'triangle',
          symbolSize: 8,
          data: catSeriesData,
        },
        {
          name: 'MTU % Change',
          color: '#1f3b61',
          symbol: 'rect',
          symbolSize: 8,
          data: mtuSeriesData,
        },
      ],
      metadata: {
        subtitle: this.buildPriceDynamicsSubtitle(),
        footerMetrics: [
          ...(Number.isFinite(latestCmi)
            ? [{
                label: 'CMI Latest:',
                value: this.formatSignedPercent(latestCmi),
                highlight: latestCmi <= 0 ? 'positive' : 'negative',
              }]
            : []),
          ...(Number.isFinite(competitorGap)
            ? [{
                label: 'vs. Best Competitor:',
                value: this.formatSignedPoints(competitorGap),
                highlight: competitorGap <= 0 ? 'positive' : 'negative',
              }]
            : []),
        ],
      },
    };
  }

  private applyPriceDynamicsPresentation(payload: any): void {
    const metadata = payload?.metadata || {};
    this.priceDynamicsSubtitle = String(
      metadata?.subtitle || this.data?.subtitle || '',
    );
    this.priceDynamicsFooterMetrics = Array.isArray(metadata?.footerMetrics)
      ? metadata.footerMetrics
      : Array.isArray(this.data?.chartConfig?.footerMetrics)
        ? this.data.chartConfig.footerMetrics
        : [];
  }

  private buildPriceDynamicsSubtitle(): string {
    const filter = this.data?.customPayload?.requestData?.filter || {};
    const machineModel = String(filter?.machine_model || '').trim();
    const customerName = String(filter?.customer_name || '').trim();
    const rangeLabel = `${this.selectedPriceDynamicsFromYear}-${this.selectedPriceDynamicsToYear}`;

    return [machineModel, customerName, rangeLabel].filter(Boolean).join(' - ');
  }

  private buildPriceDynamicsSeriesName(): string {
    const engineModelKey = String(this.data?.customPayload?.requestData?.filter?.engine_model_key || '').trim();
    return engineModelKey || 'Engine % Change';
  }

  private findLatestNumericValue(values: any[]): number {
    for (let index = values.length - 1; index >= 0; index -= 1) {
      const numericValue = Number(values[index]);
      if (Number.isFinite(numericValue)) {
        return numericValue;
      }
    }

    return NaN;
  }

  private findLatestBestCompetitor(rows: any[]): number {
    for (let index = rows.length - 1; index >= 0; index -= 1) {
      const competitorData = rows[index]?.competitorData || {};
      const candidates = [Number(competitorData?.CAT), Number(competitorData?.MTU)]
        .filter((value) => Number.isFinite(value));
      if (candidates.length > 0) {
        return Math.min(...candidates);
      }
    }

    return NaN;
  }

  private formatSignedPercent(value: number): string {
    const prefix = value > 0 ? '+' : '';
    return `${prefix}${value.toFixed(1)}%`;
  }

  private formatSignedPoints(value: number): string {
    const prefix = value > 0 ? '+' : '';
    return `${prefix}${value.toFixed(1)}pp`;
  }

  private cloneValue<T>(value: T): T {
    if (Array.isArray(value)) {
      return value.map((item) => this.cloneValue(item)) as T;
    }

    if (value && typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>).reduce((acc, [key, entryValue]) => {
        acc[key] = this.cloneValue(entryValue);
        return acc;
      }, {} as Record<string, unknown>) as T;
    }

    return value;
  }

  protected override addResizeListener(): any {
    // TODO: Add your custom logic here
    return super.addResizeListener();
  }
}
